<script setup>
import { ref } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { PhPlus, PhCaretRight } from '@phosphor-icons/vue'

import Modal   from '@pages/Shared/Modal.vue'
import Input   from '@pages/Shared/form/Input.vue'
import Select  from '@pages/Shared/form/Select.vue'
import Spinner from '@pages/Shared/ui/Spinner.vue'

import { flashToast } from '@/toast.js'
import { capitalize } from '@/helpers.js'
import { usePromotion } from '@pages/Composables/usePromotion.js'

const props = defineProps({
    modelValue:     { type: Boolean, required: true },
    sections:       { type: Array,   required: true },
    sessionOptions: { type: Array,   required: true },
})

const emit = defineEmits(['update:modelValue', 'success'])

const {
    getSectionClasses,
    getAvailableSubjects,
    changeSubject,
    commitSubject,
    cancelAddSubject,
    startAddSubject,
    removeSubject,
} = usePromotion(props.sections)

const expandedRuleId = ref(null)
const rules          = ref([])

const form = useForm({
    promotion_id:    null,
    section_id:      null,
    from_session_id: null,
    to_session_id:   null,
    class_rooms:     [],
})

const open = (promotion) => {
    const sectionId  = promotion.section?.id ?? promotion.section_id
    const classes    = getSectionClasses(sectionId)
    const classOrder = classes.map(c => c.id)

    const sortedClasses = [...(promotion.classes ?? [])].sort((a, b) => {
        const ai = classOrder.indexOf(a.from_class_room_id)
        const bi = classOrder.indexOf(b.from_class_room_id)
        return (ai === -1 ? 9999 : ai) - (bi === -1 ? 9999 : bi)
    })

    form.promotion_id    = promotion.id
    form.section_id      = sectionId
    form.from_session_id = promotion.from_session_id
    form.to_session_id   = promotion.to_session_id

    rules.value = sortedClasses.map(cr => {
        const rulesJson     = typeof cr.rules_json === 'string' ? (JSON.parse(cr.rules_json) ?? {}) : (cr.rules_json ?? {})
        const subjectRules  = rulesJson.subject_rules ?? []
        const classSubjects = cr.from_class_room?.subjects ?? []
        const fromCls       = classes.find(c => c.id == cr.from_class_room_id)
        const toCls         = classes.find(c => c.id == cr.to_class_room_id)

        return {
            from_class_id:   fromCls?.id ?? cr.from_class_room_id,
            from_class_name: cr.from_class_room?.name ?? fromCls?.name ?? '—',
            to_class_id:     toCls?.id ?? cr.to_class_room_id ?? null,
            to_class_name:   cr.to_class_room?.name ?? toCls?.name ?? 'Graduation',
            min_average:     rulesJson.average_score ?? cr.average_score ?? 0,
            subjects: subjectRules.map(sr => {
                const subject = classSubjects.find(s => s.id === sr.subject_id)
                    ?? fromCls?.subjects?.find(s => s.id === sr.subject_id)
                return {
                    id:        subject?.id ?? sr.subject_id,
                    name:      subject?.name ?? '—',
                    min_score: sr.min_score,
                }
            }),
            addingSubject: false,
            draft:         { subject_id: null, min_score: 40 },
            errors:        {},
        }
    })

    expandedRuleId.value = rules.value[0]?.from_class_id ?? null
    emit('update:modelValue', true)
}

const toggleRuleCard = (classId) => {
    expandedRuleId.value = expandedRuleId.value === classId ? null : classId
}

const handleSubmit = () => {
    form.class_rooms = rules.value.map(rule => ({
        from_class_id: rule.from_class_id,
        to_class_id:   rule.to_class_id,
        average_score: rule.min_average,
        subject_rules: rule.subjects.map(s => ({ subject_id: s.id, min_score: s.min_score })),
    }))

    form.put(`/staff/manage-promotions/${form.promotion_id}`, {
        onSuccess: (page) => {
            close()
            emit('success')
            flashToast(page.props.flash)
        },
        onError: () => {
            flashToast({ type: 'error', message: 'Please check the form for errors' })
        },
    })
}

const close = () => {
    emit('update:modelValue', false)
    expandedRuleId.value = null
    rules.value          = []
    form.reset()
}

defineExpose({ open })
</script>

<template>
    <Modal :model-value="modelValue" title="Edit Promotion" size="medium" @close="close">
        <form class="p-6" @submit.prevent="handleSubmit">
            <p class="text-sm text-gray-500 mb-1">Update session assignments and per-class promotion rules.</p>
            <p class="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-md px-3 py-2 mb-6">
                Saving will re-score all students against the updated rules and move them accordingly.
            </p>

            <div class="grid grid-cols-2 gap-4 mb-6">
                <Select label="From Session" v-model="form.from_session_id"
                    :options="sessionOptions" placeholder="Select session" :error="form.errors.from_session_id" />
                <Select label="To Session" v-model="form.to_session_id"
                    :options="sessionOptions" placeholder="Select session" :error="form.errors.to_session_id" />
            </div>

            <div class="space-y-3">
                <div v-for="(rule, index) in rules" :key="rule.from_class_id"
                    class="border border-gray-200 rounded-lg overflow-hidden">

                    <div class="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 transition"
                        @click="toggleRuleCard(rule.from_class_id)">
                        <div class="flex items-center gap-2.5">
                            <span class="w-6 h-6 flex items-center justify-center bg-primary text-white text-xs font-bold rounded-full shrink-0">
                                {{ index + 1 }}
                            </span>
                            <span class="text-sm font-semibold text-gray-800">
                                {{ capitalize(rule.from_class_name) }}
                                <span class="text-gray-400 mx-1">→</span>
                                <span :class="!rule.to_class_id ? 'text-edit' : 'text-gray-800'">
                                    {{ capitalize(rule.to_class_name) }}
                                </span>
                            </span>
                        </div>
                        <PhCaretRight :size="14" class="text-gray-400 transition-transform duration-200"
                            :class="expandedRuleId === rule.from_class_id ? 'rotate-90' : ''" />
                    </div>

                    <div v-show="expandedRuleId === rule.from_class_id"
                        class="px-4 pb-4 pt-3 border-t border-gray-100 space-y-4 bg-gray-50/50">

                        <div>
                            <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                                Minimum Average Score
                            </label>
                            <Input type="number" v-model="rule.min_average" min="0" max="100" placeholder="0" />
                            <p v-if="rule.errors?.average_score" class="text-xs text-red-500 mt-1">{{ rule.errors.average_score }}</p>
                        </div>

                        <div class="space-y-2">
                            <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide block">
                                Subject Minimum Scores
                            </label>

                            <div v-for="(subject, si) in rule.subjects" :key="subject.id" class="flex items-center gap-2">
                                <Select
                                    :model-value="subject.id"
                                    :options="getAvailableSubjects(form.section_id, rule.from_class_id, rule.subjects, subject.id)"
                                    class="flex-1"
                                    @update:modelValue="changeSubject(rule, si, $event, form.section_id)" />
                                <Input type="number" v-model="subject.min_score" class="w-20" min="0" max="100" />
                                <button type="button" class="text-gray-300 hover:text-red-500 text-xl leading-none transition"
                                    @click="removeSubject(rule, subject.id)">&times;</button>
                            </div>

                            <p v-if="rule.errors?.subject_rules" class="text-xs text-red-500">{{ rule.errors.subject_rules }}</p>

                            <div v-if="rule.addingSubject" class="flex items-center gap-2">
                                <Select v-model="rule.draft.subject_id"
                                    :options="getAvailableSubjects(form.section_id, rule.from_class_id, rule.subjects)"
                                    placeholder="Select subject" class="flex-1"
                                    @update:modelValue="commitSubject(rule, form.section_id)" />
                                <Input type="number" v-model="rule.draft.min_score" class="w-20" min="0" max="100" />
                                <button type="button" class="text-gray-300 hover:text-red-500 text-xl leading-none transition"
                                    @click="cancelAddSubject(rule)">&times;</button>
                            </div>

                            <button v-if="!rule.addingSubject" type="button"
                                class="text-xs font-medium text-primary hover:text-primary-light flex items-center gap-1 mt-1 transition"
                                @click="startAddSubject(rule, form.section_id)">
                                <PhPlus :size="14" /> Add Subject Threshold
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex items-center gap-3 pt-5 border-t border-gray-100 mt-6">
                <button type="button" @click="close"
                    class="flex-1 px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-200
                           hover:bg-gray-300 rounded-lg transition">
                    Cancel
                </button>
                <button type="submit" :disabled="form.processing"
                    class="flex-1 px-5 py-2.5 text-sm font-medium text-white bg-edit hover:bg-edit-light
                           rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed
                           flex justify-center items-center gap-2">
                    <Spinner v-if="form.processing" :size="14" /> Re-score & Save
                </button>
            </div>
        </form>
    </Modal>
</template>